/**
 * Per-tab memory of manual MPR orientation corrections, keyed by study.
 *
 * When a study's PatientPosition tag is wrong (human position entered for a
 * laterally recumbent animal), the user fixes it with the Reorient controls.
 * That correction should survive closing and reopening the MPR on the same
 * study in this tab — but it must NOT be persisted globally: the tags of the
 * next study may be perfectly fine, and a synced "always roll 90°" flag would
 * silently corrupt orientation everywhere. sessionStorage keeps it per tab;
 * clearOrientationOverrideMemory() must be called from the auth logout block
 * so a shared workstation never replays one user's correction to the next.
 */
import { isIdentityOverride } from '@/lib/cornerstone/vetOrientation';
const ORIENTATION_OVERRIDE_KEY = 'viewer.mprOrientationOverrides';
function isValidOverride(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const v = value;
    return (typeof v.quarterTurns === 'number' &&
        Number.isInteger(v.quarterTurns) &&
        typeof v.noseFlipped === 'boolean');
}
function readMap() {
    try {
        const raw = sessionStorage.getItem(ORIENTATION_OVERRIDE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))
            return {};
        const result = {};
        for (const [studyId, value] of Object.entries(parsed)) {
            if (isValidOverride(value)) {
                result[studyId] = { quarterTurns: value.quarterTurns, noseFlipped: value.noseFlipped };
            }
        }
        return result;
    }
    catch {
        return {};
    }
}
function writeMap(map) {
    try {
        sessionStorage.setItem(ORIENTATION_OVERRIDE_KEY, JSON.stringify(map));
    }
    catch {
        // Storage unavailable — the correction simply won't restore; never throw.
    }
}
export function getOrientationOverride(studyId) {
    return readMap()[studyId] ?? null;
}
/** Store a study's correction; an identity correction removes the entry. */
export function rememberOrientationOverride(studyId, override) {
    const map = readMap();
    if (isIdentityOverride(override)) {
        if (!(studyId in map))
            return;
        delete map[studyId];
    }
    else {
        map[studyId] = {
            quarterTurns: ((override.quarterTurns % 4) + 4) % 4,
            noseFlipped: override.noseFlipped,
        };
    }
    writeMap(map);
}
export function clearOrientationOverrideMemory() {
    try {
        sessionStorage.removeItem(ORIENTATION_OVERRIDE_KEY);
    }
    catch {
        // Storage unavailable — nothing was stored anyway.
    }
}
